/**
 * Il giudizio di accessibilita' di un file appena caricato, con che cosa fare.
 *
 * Sempre visibili restano solo il giudizio e il numero degli errori: l'elenco,
 * la fonte e l'intervento stanno nel menu di `ErroriRilevati`, chiuso.
 *
 * Il caricamento rispondeva «documento caricato in bozza» e basta: il giudizio
 * c'era gia' nella risposta dell'API, ma chi aveva caricato il file lo
 * scopriva solo andandolo a cercare in un'altra pagina, a volte dopo averlo
 * pubblicato. E' il momento sbagliato per scoprirlo — il momento giusto e'
 * questo, con il file originale ancora sotto mano.
 *
 * Su `non_verificata` offre «Riprova»: e' l'unico gesto che quello stato
 * ammette, e senza il pulsante qui l'operatore doveva sapere che esiste una
 * pagina dove farlo. La riverifica funziona anche su un documento in bozza.
 *
 * Su un formato aperto (`non_applicabile`) non mostra nulla: non c'e' un
 * controllo da riferire, e un riquadro che lo dicesse dopo ogni CSV sarebbe
 * rumore.
 */

import { useState } from "react";
import { ErroreApi, riverificaDocumento } from "../api/client";
import type { Documento } from "../api/types";
import { esitoRiverifica, statoDi } from "../lib/accessibilita";
import { ErroriRilevati } from "./ErroriRilevati";
import { Bottone, ChipAccessibilita } from "./primitive";

export function EsitoAccessibilita({ documento }: { documento: Documento }) {
  // Il documento si tiene in stato locale: dopo «Riprova» il giudizio nuovo
  // sostituisce quello vecchio qui, senza che chi ha montato il riquadro debba
  // saperlo.
  const [corrente, setCorrente] = useState(documento);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  // Che cosa ha prodotto l'ultimo «Riprova», anche quando nulla e' cambiato.
  const [ripetuto, setRipetuto] = useState<string | null>(null);

  const stato = statoDi(corrente);
  if (stato === "non_applicabile") return null;

  async function riprova() {
    setErrore(null);
    setRipetuto(null);
    setInCorso(true);
    try {
      const aggiornato = await riverificaDocumento(corrente.id);
      setRipetuto(
        esitoRiverifica(
          aggiornato.titolo,
          statoDi(corrente),
          statoDi(aggiornato),
          corrente.copie_collegate ?? [],
        ),
      );
      setCorrente(aggiornato);
    } catch (causa) {
      setErrore(causa instanceof ErroreApi ? causa.messaggioEsteso : "Il controllo non e' ripartito.");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="esito-accessibilita" aria-live="polite">
      <div className="riga">
        <span>Accessibilita' di «{corrente.titolo}»:</span>
        <ChipAccessibilita stato={stato} />
      </div>
      <ErroriRilevati documento={corrente} />
      {stato === "non_verificata" ? (
        <div>
          <Bottone
            variante="contorno"
            dimensione="piccolo"
            inCorso={inCorso}
            onClick={() => void riprova()}
          >
            Riprova
          </Bottone>
        </div>
      ) : null}
      {ripetuto ? <p className="campo__aiuto">{ripetuto}</p> : null}
      {errore ? <p className="campo__errore">{errore}</p> : null}
    </div>
  );
}
